import { PartialDesignTokens } from './design-tokens.interface';

// ── Assets ──────────────────────────────────────────────────

export interface TenantAssets {
  logoUrl: string | null;
  logoSmallUrl: string | null;
  faviconUrl: string | null;
}

export const DEFAULT_TENANT_ASSETS: TenantAssets = {
  logoUrl: 'assets/img/logo_polipay.svg',
  logoSmallUrl: 'assets/img/logo_small.svg',
  faviconUrl: null,
};

// ── Wording (key brand terms, NOT full i18n) ────────────────

export interface TenantWording {
  appName: string;
  appNameLower: string;
  companyLabel: string;
  [key: string]: string;
}

// ── Feature flags ───────────────────────────────────────────

export interface TenantFeatures {
  showReports: boolean;
  showExport: boolean;
  transactions: boolean;
  cards: boolean;
  ecommerce: boolean;
  referencePayments: boolean;
  qrWallet: boolean;
  tpv: boolean;
  administration: boolean;
  dispersions: boolean;
  enterprise: boolean;
  [key: string]: boolean;
}

// ── Full API response ───────────────────────────────────────

/**
 * Response from the tenant theme API endpoint.
 * GET /api/v1/tenants/{tenantId}/theme
 */
export interface TenantThemeApiResponse {
  tenantId: string;
  tokens: PartialDesignTokens | null;
  assets: TenantAssets | null;
  wording: TenantWording | null;
  features: TenantFeatures | null;
  /**
   * Solo lo devuelve el endpoint POR HOST (`/tenants/theme/by-host/`):
   * `'host'` = resolvió contra su tabla de dominios · `'default'` = ningún
   * dominio calzó y cayó a `polipay`. Es lo que permite distinguir "esta marca
   * ES la del host" de "no se encontró nada", sin deducirlo del `tenantId`.
   */
  resolvedBy?: 'host' | 'default';
  /**
   * Solo lo devuelve el endpoint POR HOST. `true` = el hostname desde el que se
   * abrió la app cae bajo un nivel que administramos para marcas (el back lo
   * deriva de `TENANT_DNS_ALLOWED_SUFFIXES`), así que un `resolvedBy: 'default'`
   * ahí NO es "un host cualquiera de la suite": es un dominio del nivel que
   * nadie tiene asignado — típicamente uno que se acaba de dar de BAJA y que el
   * navegador sigue resolviendo por su caché de DNS.
   *
   * ⚠️ OPCIONAL a propósito y se lee FALLANDO ABIERTO: un back anterior no lo
   * manda y ahí no hay nada que bloquear. Ver `dominioNoAsignado` en
   * `TenantThemeService`.
   */
  hostGestionado?: boolean;
}

// ── Defaults ────────────────────────────────────────────────

export const DEFAULT_TENANT_WORDING: TenantWording = {
  appName: 'Polipay',
  appNameLower: 'polipay',
  companyLabel: 'Empresa',
  contactPhone: '+52 55 4170 4129',
  contactEmail: 'contacto@polipay.mx',
  supportUrl: 'https://soporte.polipay.io',
};

export const DEFAULT_TENANT_FEATURES: TenantFeatures = {
  showReports: true,
  showExport: true,
  entities: true,
  dashboard: true,
  transactions: true,
  polipayQR: true,
  cards: true,
  ecommerce: true,
  referencePayments: true,
  qrWallet: true,
  tpv: true,
  administration: true,
  dispersions: true,
  enterprise: true,
};

// ── Deprecated (backward compat) ────────────────────────────

/**
 * @deprecated Use TenantThemeApiResponse instead.
 */
export interface TenantThemeResponse {
  theme: PartialDesignTokens | null;
  logo_url: string | null;
}
